// Resolve a maintenance ticket: optional resolution note + up to 3 "after" photos so the owner sees the repair was done.
import React, { useState } from 'react';
import { Check, Loader2, Wrench, X } from 'lucide-react';
import { apiCall } from './_shared';
import { TicketPhotoPicker, TicketPhotoStrip, photosByPhase, uploadTicketPhotos } from './TicketPhotos';

const ResolveTicketModal = ({ ticket, onClose, onDone }) => {
  const [note, setNote] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const { before } = photosByPhase(ticket.photos);
  const submit = async () => {
    setBusy(true); setErr('');
    try {
      if (files.length) {
        const up = await uploadTicketPhotos(ticket.id, files, 'after');
        if (up.errors.length) throw new Error(up.errors.join('; '));
      }
      await apiCall(`/api/admin/housekeeping/tickets/${ticket.id}`, { method: 'PUT', body: JSON.stringify({ status: 'resolved', resolution: note.trim() || null }) });
      onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="hk-resolve-modal">
      <div className="bg-white rounded-xl w-full max-w-md p-5 shadow-2xl space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-bold flex items-center gap-2"><Wrench className="w-4 h-4 text-emerald-600" /> Resolve ticket</h3>
            <p className="text-xs text-slate-500 mt-0.5">{ticket.unit_name ? `Room ${ticket.unit_name} · ` : ''}{ticket.room_name || ''} · {ticket.issue}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="hk-resolve-close"><X className="w-5 h-5" /></button>
        </div>
        {before.length > 0 && <div><div className="text-[10px] uppercase text-red-700 font-semibold">Damage photos</div><TicketPhotoStrip photos={before} testId="hk-resolve-before" /></div>}
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="What was done? (e.g. replaced the shower hose, tested — no leak)" className="w-full border rounded px-3 py-2 text-sm" data-testid="hk-resolve-note" />
        <div>
          <div className="text-[10px] uppercase text-emerald-700 font-semibold mb-1">After photos (optional)</div>
          <TicketPhotoPicker files={files} onChange={setFiles} testId="hk-resolve-photos" hint="after photos — the owner sees the repair was done" />
        </div>
        {err && <div className="text-xs text-red-700" data-testid="hk-resolve-error">{err}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm hover:bg-gray-100 rounded">Cancel</button>
          <button type="button" onClick={submit} disabled={busy} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-1" data-testid="hk-resolve-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Mark resolved{files.length ? ` · ${files.length} photo(s)` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResolveTicketModal;
